import * as React from 'react';
import classnames from 'classnames';
import FilterWrapper from "../../FilterWrapper";
import { RadioFacet } from "./RadioFacet";
import { CheckboxFacet } from "./CheckboxFacet";
import { DropdownFacet } from "./DropdownFacet";
import { AxisSwitcherFilter } from "./AxisSwitcherFilter";
import { GroupedDropdownFilter } from "./GroupedDropdownFilter";
import { Facet, useSearch } from "@elastic/react-search-ui";

// A facet has data when at least one of its values has a non-zero count for the
// current selection. Used to hide a facet that would be empty for the current
// selection — e.g. a subcategory facet when the selected parent has none.
function useFacetHasData(attribute) {
  const { facets } = useSearch();
  const data = facets?.[attribute]?.[0]?.data || [];
  return data.some(b => b.count > 0);
}

function CheckboxesFilter({label, attribute, initialFacetValues, isFirstFacet, order}) {
  // Stable wrapper so the admin's manual value order reaches the view without
  // remounting it on every render.
  const view = React.useMemo(() => (viewProps) => <CheckboxFacet {...viewProps} order={order}/>, [order]);
  if (!useFacetHasData(attribute)) return null;
  return (
    <FilterWrapper label={label} attribute={attribute} kind='checkboxes'>
      <Facet field={attribute} label={label} filterType='any' show={500} view={view} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>
    </FilterWrapper>
  );
}

function DropdownFilter({label, attribute, initialFacetValues, isFirstFacet}) {
  if (!useFacetHasData(attribute)) return null;
  return (
    <FilterWrapper label={label} attribute={attribute} kind='dropdown'>
      <Facet field={attribute} label={label} filterType='any' show={500} view={DropdownFacet} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>
    </FilterWrapper>
  );
}

function RadioButtonsFilter({label, attribute, initialFacetValues, isFirstFacet}) {
  if (!useFacetHasData(attribute)) return null;
  return (
    <FilterWrapper label={label} attribute={attribute} kind='radio-buttons'>
      <Facet field={attribute} label={label} filterType='any' show={500} view={RadioFacet} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>
    </FilterWrapper>
  );
}

export default function Filter(props) {
  const {config, isFirstFacet, allValues = {}, collectionName, sharedSelection = null} = props;
  const {
    attribute = null,
    label,
    kind,
  } = config;

  // Axis / grouped controls — not single-field search facets, so they are
  // handled before the `attribute` guard below.
  if (kind === 'axis-switcher') {
    return <AxisSwitcherFilter label={label}/>;
  }
  if (kind === 'grouped-dropdown') {
    return <GroupedDropdownFilter
             label={label}
             sections={config.sections}
             valueLabels={config.valueLabels}
             scopeAttribute={config.scopeAttribute}
             collectionName={collectionName}
             sharedSelection={sharedSelection}/>;
  }

  if (!attribute) {
    return null;
  }

  const initialFacetValues = allValues[attribute] || [];

  switch (kind) {
    case 'radio':
      return <RadioButtonsFilter
               label={label}
               attribute={attribute}
               initialFacetValues={initialFacetValues}
               isFirstFacet={isFirstFacet}/>;
    case 'checkboxes':
      return <CheckboxesFilter
               label={label}
               attribute={attribute}
               initialFacetValues={initialFacetValues}
               isFirstFacet={isFirstFacet}
               order={config.order}/>;
    case 'dropdown':
      return <DropdownFilter
               label={label}
               attribute={attribute}
               initialFacetValues={initialFacetValues}
               isFirstFacet={isFirstFacet}/>;
    default:
      return null;
  }
}
